import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Button } from 'components/Button/Button'
import InputField from 'components/Form/InputField/InputField'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import { setPassword } from 'services/endpoints/auth'
import { useSetUserProfile } from 'store'

interface Props {
    disconnectSocialAccount?: (mediaName: 'google' | 'twitter') => Promise<void>
    mediaName?: 'google' | 'twitter'
    disconnectAccout?: boolean
}

export const SetPassword = ({
    disconnectSocialAccount,
    mediaName,
    disconnectAccout = false
}: Props) => {
    const { t } = useTranslation()
    const modalData = useModalContext()

    const [passwordInputType, setPasswordInputType] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const [passState, setPassState] = useState({
        new: '',
        confirm: ''
    })

    const validatePassword = (password: string) => {
        return (
            password.length >= 8 &&
            /[a-z]/.test(password) &&
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password)
        )
    }

    const passwordsMatch =
        passState.new === passState.confirm &&
        passState.confirm.length > 0

    const canSubmit =
        validatePassword(passState.new) &&
        passwordsMatch &&
        !submitting

    const submitForm = async () => {
        if (submitting) return

        if (!validatePassword(passState.new)) {
            addToast(
                'error',
                'Password must contain at least 8 characters, uppercase and lowercase letters, and a number.'
            )
            return
        }

        if (!passwordsMatch) {
            addToast('error', t('error:errorPasswordsDontMatch'))
            return
        }

        setSubmitting(true)

        try {
            await setPassword({
                password: passState.new,
                password_confirmation: passState.confirm
            })

            // Update the existing Zustand profile immediately.
            useSetUserProfile({
                has_password: true
            })

            if (disconnectAccout && mediaName) {
                if (!disconnectSocialAccount) {
                    throw new Error('Disconnect handler is missing')
                }

                await disconnectSocialAccount(mediaName)
            }

            modalData.clearModal()

            if (!disconnectAccout) {
                addToast('success', 'Password set successfully')
            }
        } catch (error) {
            console.error('Set password error:', error)
            addToast('error', t('error:errorInvalidPasswordReset'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className='setPassword_modal'>
            <div className='auth-reset-password'>
                <h1 className='auth-reset-password__title'>
                    Set Password
                </h1>

                {disconnectAccout && (
                    <p className='auth-reset-password__subtitle'>
                        In order to disconnect your{' '}
                        {mediaName === 'twitter' ? 'X' : 'Google'} account,
                        please set a password.
                    </p>
                )}

                <div className='auth-reset-password__form'>
                    <InputField
                        customClass='auth-reset-password__new-password'
                        id='new-password'
                        type={passwordInputType ? 'password' : 'text'}
                        value={passState.new}
                        changeFn={(val: string) => {
                            setPassState(prev => ({
                                ...prev,
                                new: val
                            }))
                        }}
                        inputvisible={passwordInputType}
                        swaptypeicon={true}
                        swaptype={() => {
                            setPasswordInputType(prev => !prev)
                        }}
                        validate='required|password'
                        validationErrorMessage={t('error:errorPasswordReset')}
                        validationCharactersNumber={8}
                        additionalProps={{
                            placeholder: t('Password'),
                            autoComplete: 'new-password'
                        }}
                    />

                    <InputField
                        id='confirm-password'
                        type={passwordInputType ? 'password' : 'text'}
                        value={passState.confirm}
                        changeFn={(val: string) => {
                            setPassState(prev => ({
                                ...prev,
                                confirm: val
                            }))
                        }}
                        inputvisible={passwordInputType}
                        swaptypeicon={true}
                        swaptype={() => {
                            setPasswordInputType(prev => !prev)
                        }}
                        validate='required|password'
                        validationErrorMessage={t('error:errorPasswordReset')}
                        validationCharactersNumber={8}
                        additionalProps={{
                            placeholder: t('confirmPassword'),
                            autoComplete: 'new-password'
                        }}
                    />

                    <div className='auth-reset-password__buttons'>
                        <Button
                            text={
                                submitting
                                    ? 'Saving...'
                                    : disconnectAccout
                                      ? 'Continue to Disconnect'
                                      : 'Set'
                            }
                            color='blue'
                            font='mont-16-semi-bold'
                            width='100'
                            height='5'
                            clickFn={() => {
                                if (canSubmit) {
                                    void submitForm()
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}